import { resolveBaseUrl, createChatClient, getWidgetConfig, createChanlClient, createChanlRuntime } from '../chunk-G32KXWAY.mjs';
import '../chunk-7H37YIE3.mjs';
import '../chunk-NFEGQTCC.mjs';
import { useRef, useEffect, useState, useCallback } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';

function ChanlWidget(props) {
  const {
    pubKey,
    agentId,
    user,
    userHash,
    baseUrl,
    chatHost,
    theme,
    position,
    color,
    branding,
    agentName,
    agentSubtitle,
    agentInitials,
    agentAvatarUrl,
    greeting,
    placeholder,
    darkBg,
    privacyUrl,
    locale
  } = props;
  const latestPropsRef = useRef(props);
  latestPropsRef.current = props;
  const destroyTimer = useRef(null);
  useEffect(
    () => {
      if (typeof window === "undefined") return;
      if (destroyTimer.current) {
        clearTimeout(destroyTimer.current);
        destroyTimer.current = null;
      }
      let cancelled = false;
      const build = () => {
        const p = latestPropsRef.current;
        return {
          agentId: p.agentId,
          apiKey: p.pubKey,
          user: p.user,
          userHash: p.userHash,
          baseUrl: p.baseUrl,
          chatHost: p.chatHost,
          theme: p.theme,
          position: p.position,
          color: p.color,
          branding: p.branding,
          agentName: p.agentName,
          agentSubtitle: p.agentSubtitle,
          agentInitials: p.agentInitials,
          agentAvatarUrl: p.agentAvatarUrl,
          greeting: p.greeting,
          placeholder: p.placeholder,
          darkBg: p.darkBg,
          privacyUrl: p.privacyUrl,
          locale: p.locale
        };
      };
      void import('../loader-TLLD7TJZ.mjs').then(() => {
        if (cancelled) return;
        const ChanlChat = window.ChanlChat;
        if (!ChanlChat) return;
        if (ChanlChat._initialized) {
          try {
            ChanlChat.destroy();
          } catch {
          }
        }
        ChanlChat.init(build());
      });
      return () => {
        cancelled = true;
        destroyTimer.current = setTimeout(() => {
          if (typeof window !== "undefined") {
            const live = window.ChanlChat;
            if (live && live._initialized) {
              try {
                live.destroy();
              } catch {
              }
            }
          }
          destroyTimer.current = null;
        }, 150);
      };
    },
    // Primitive-only dependencies per react-best-practices `rerender-dependencies`.
    // Re-init fires when any of these change. Non-primitive user fields
    // (email, name, avatarUrl, attributes) flow via the ref on the next
    // init — they don't trigger re-init on their own.
    [
      agentId,
      pubKey,
      user?.externalId,
      userHash,
      baseUrl,
      chatHost,
      theme,
      position,
      color,
      branding,
      agentName,
      agentSubtitle,
      agentInitials,
      agentAvatarUrl,
      greeting,
      placeholder,
      darkBg,
      privacyUrl,
      locale
    ]
  );
  return null;
}
var DEFAULT_CHAT_HOST = "https://chat.channel.tel";
var IDENTITY_POLL_TIMEOUT_MS = 8e3;
function getChanlChat() {
  return window.ChanlChat;
}
function ChanlEmbed(props) {
  const { workspace, chatHost, user, userHash } = props;
  const latest = useRef(props);
  latest.current = props;
  const destroyTimer = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined" || !workspace) return;
    if (destroyTimer.current) {
      clearTimeout(destroyTimer.current);
      destroyTimer.current = null;
    }
    const host = (chatHost ?? DEFAULT_CHAT_HOST).replace(/\/$/, "");
    const src = `${host}/w/${encodeURIComponent(workspace)}.js`;
    document.querySelectorAll("script[data-chanl-embed]").forEach((el) => el.remove());
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    script.setAttribute("data-chanl-embed", workspace);
    document.body.appendChild(script);
    let cancelled = false;
    const startedAt = Date.now();
    const applyIdentity = () => {
      if (cancelled) return;
      const cc = getChanlChat();
      const p = latest.current;
      if (cc && cc._initialized) {
        if (p.user || p.userHash) {
          cc.setUser({ ...p.user ?? {}, userHash: p.userHash });
        }
        return;
      }
      if (Date.now() - startedAt < IDENTITY_POLL_TIMEOUT_MS) {
        window.setTimeout(applyIdentity, 120);
      }
    };
    if (user || userHash) applyIdentity();
    return () => {
      cancelled = true;
      destroyTimer.current = setTimeout(() => {
        const cc = getChanlChat();
        if (cc && cc._initialized) {
          try {
            cc.destroy();
          } catch {
          }
        }
        document.querySelectorAll("script[data-chanl-embed]").forEach((el) => el.remove());
        destroyTimer.current = null;
      }, 150);
    };
  }, [workspace, chatHost, user?.externalId, userHash]);
  return null;
}
var counter = 0;
function id() {
  return `sdk-${++counter}-${Date.now()}`;
}
function toolLabel(toolName, state) {
  const labels = {
    search_listings: { call: "Searching listings...", result: "\u2713 Found results" },
    lookup_order: { call: "Looking up order...", result: "\u2713 Order found" },
    get_available_slots: { call: "Checking availability...", result: "\u2713 Slots found" },
    check_order_status: { call: "Checking order status...", result: "\u2713 Status retrieved" },
    calendar_booking: { call: "Booking appointment...", result: "\u2713 Booked" },
    send_email: { call: "Sending email...", result: "\u2713 Email sent" },
    knowledge_search: { call: "Searching knowledge base...", result: "\u2713 Found answers" }
  };
  const entry = labels[toolName] ?? { call: "Working on it...", result: "\u2713 Done" };
  return state === "call" ? entry.call : entry.result;
}
function useChat({
  agentId,
  apiKey,
  baseUrl,
  greeting,
  user,
  userHash,
  variables,
  metadata
}) {
  const resolvedBaseUrl = resolveBaseUrl(baseUrl);
  const clientRef = useRef(createChatClient({ apiKey, baseUrl: resolvedBaseUrl }));
  const sessionRef = useRef(null);
  const isStreamingRef = useRef(false);
  const identityRef = useRef({ user, userHash, variables, metadata });
  identityRef.current = { user, userHash, variables, metadata };
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [agentConfig, setAgentConfig] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getWidgetConfig(agentId, apiKey, resolvedBaseUrl).then((config) => {
      if (cancelled || !config) return;
      const agent = {
        id: agentId,
        name: config.agent?.name ?? "AI Assistant",
        initials: (config.agent?.name ?? "AI").split(" ").map((w) => w[0]).join("").slice(0, 2),
        avatarUrl: config.style?.agentAvatar,
        greeting: greeting ?? config.banner?.description ?? `Hi! I'm ${config.agent?.name ?? "here to help"}. How can I assist you?`,
        suggestions: [],
        primaryColor: config.style?.primaryColor
      };
      setAgentConfig(agent);
      setMessages([{ id: id(), role: "assistant", content: agent.greeting, timestamp: Date.now() }]);
    });
    return () => {
      cancelled = true;
    };
  }, [agentId, apiKey, baseUrl, greeting]);
  const ensureSession = useCallback(async () => {
    if (sessionRef.current) return sessionRef.current;
    const { user: user2, userHash: userHash2, variables: variables2, metadata: metadata2 } = identityRef.current;
    const session = await clientRef.current.createSession(agentId, {
      user: user2,
      userHash: userHash2,
      variables: variables2,
      metadata: metadata2
    });
    sessionRef.current = session;
    setSessionReady(true);
    return session;
  }, [agentId]);
  const externalId = user?.externalId;
  useEffect(() => {
    if (sessionRef.current) {
      const stale = sessionRef.current;
      sessionRef.current = null;
      setSessionReady(false);
      clientRef.current.endSession(stale.sessionId).catch(() => {
      });
    }
  }, [externalId]);
  const send = useCallback(async (text) => {
    if (isStreamingRef.current) return;
    setError(null);
    setMessages((p) => [...p, { id: id(), role: "user", content: text, timestamp: Date.now() }]);
    setIsTyping(true);
    try {
      const session = await ensureSession();
      let placeholderId = null;
      const startTime = Date.now();
      isStreamingRef.current = true;
      const statusIds = [];
      const response = await clientRef.current.streamMessage(
        session.sessionId,
        text,
        // onChunk — text delta
        (chunk) => {
          if (!placeholderId) {
            if (!chunk) return;
            const newId = id();
            placeholderId = newId;
            setIsTyping(false);
            setMessages((p) => [
              ...p,
              { id: newId, role: "assistant", content: chunk, timestamp: Date.now() }
            ]);
            return;
          }
          setMessages((p) => p.map(
            (m) => m.id === placeholderId ? { ...m, content: m.content + chunk } : m
          ));
        },
        // onPart — tool invocations
        (part) => {
          if (part.type !== "tool-invocation" || !part.toolInvocation) return;
          const inv = part.toolInvocation;
          const label = toolLabel(inv.toolName, inv.state);
          if (inv.state === "call") {
            const sid = id();
            statusIds.push(sid);
            setMessages((p) => {
              const idx = p.findIndex((m) => m.id === placeholderId);
              const pill = { id: sid, role: "status", content: label, label, timestamp: Date.now() };
              if (idx >= 0) {
                const next = [...p];
                next.splice(idx, 0, pill);
                return next;
              }
              return [...p, pill];
            });
          } else if (inv.state === "result") {
            const lastSid = statusIds[statusIds.length - 1];
            if (lastSid) {
              setMessages((p) => p.map(
                (m) => m.id === lastSid ? { ...m, content: label, label } : m
              ));
            }
          }
        }
      );
      isStreamingRef.current = false;
      const elapsed = (Date.now() - startTime) / 1e3;
      const toolCount = statusIds.length;
      if (!placeholderId && response.message) {
        const newId = id();
        placeholderId = newId;
        setIsTyping(false);
      }
      const finalPlaceholderId = placeholderId;
      setMessages((p) => {
        let next = p.filter((m) => !statusIds.includes(m.id));
        if (finalPlaceholderId) {
          const exists = next.some((m) => m.id === finalPlaceholderId);
          if (exists) {
            next = next.map(
              (m) => m.id === finalPlaceholderId ? { ...m, content: response.message } : m
            );
          } else {
            next = [
              ...next,
              { id: finalPlaceholderId, role: "assistant", content: response.message, timestamp: Date.now() }
            ];
          }
        }
        if (toolCount > 0 && finalPlaceholderId) {
          const badgeIdx = next.findIndex((m) => m.id === finalPlaceholderId);
          if (badgeIdx >= 0) {
            next.splice(badgeIdx, 0, {
              id: id(),
              role: "tool",
              content: `Thought for ${elapsed.toFixed(1)}s \xB7 Used ${toolCount} tool${toolCount > 1 ? "s" : ""}`,
              timestamp: Date.now(),
              duration: elapsed
            });
          }
        }
        return next;
      });
    } catch (err) {
      isStreamingRef.current = false;
      setError(err instanceof Error ? err.message : "Something went wrong");
      setIsTyping(false);
    }
  }, [ensureSession]);
  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        clientRef.current.endSession(sessionRef.current.sessionId);
      }
    };
  }, []);
  return { messages, isTyping, error, send, sessionReady, agentConfig };
}
function useVoice(opts) {
  const { apiKey, debug = false, baseUrl, anonymousId, user, userHash, transport } = opts;
  const voiceRef = useRef(null);
  const [mode, setMode] = useState("idle");
  const [status, setStatus] = useState("idle");
  const [isMuted, setIsMuted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [lastError, setLastError] = useState();
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [duration, setDuration] = useState(0);
  const timerRef = useRef(null);
  const currentAgentIdRef = useRef(null);
  const pushTranscript = useCallback((transcript) => {
    if (!transcript?.text) return;
    const sender = transcript.sender === "assistant" ? "agent" : "user";
    const content = transcript.text;
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.sender === sender) {
        if (last.content === content) return prev;
        if (content.startsWith(last.content) || last.content.startsWith(content)) {
          const longer = content.length >= last.content.length ? content : last.content;
          const replaced = { ...last, content: longer };
          return [...prev.slice(0, -1), replaced];
        }
      }
      return [
        ...prev,
        {
          id: (transcript.timestamp || Date.now()).toString(),
          sender,
          content,
          timestamp: new Date(transcript.timestamp || Date.now())
        }
      ];
    });
  }, []);
  const addMessage = useCallback((sender, content) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        sender,
        content,
        timestamp: /* @__PURE__ */ new Date()
      }
    ]);
  }, []);
  const bindVoiceEvents = useCallback(() => {
    const v = voiceRef.current;
    if (!v) return void 0;
    const onReady = () => {
      if (debug) console.debug("[useVoice] onReady");
      setStatus("active");
      setDuration(0);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1e3);
    };
    const onClose = (code, reason) => {
      if (debug) console.debug("[useVoice] onClose", { code, reason });
      setStatus("closed");
      setMode("idle");
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    const onError = (e) => {
      console.error("[useVoice] onError", e);
      setLastError(e);
      setStatus("error");
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    const onTranscript = (transcript) => {
      if (debug) console.debug("[useVoice] onTranscript", transcript);
      pushTranscript(transcript);
    };
    const onMicMuted = () => {
      if (debug) console.debug("[useVoice] onMicMuted");
      setIsMuted(true);
    };
    const onMicUnmuted = () => {
      if (debug) console.debug("[useVoice] onMicUnmuted");
      setIsMuted(false);
    };
    const onSpeechStart = () => {
      if (debug) console.debug("[useVoice] onSpeechStart");
      setIsAgentSpeaking(true);
    };
    const onSpeechEnd = () => {
      if (debug) console.debug("[useVoice] onSpeechEnd");
      setIsAgentSpeaking(false);
    };
    v.on("ready", onReady);
    v.on("closed", onClose);
    v.on("error", onError);
    v.on("transcript", onTranscript);
    v.on("mic-muted", onMicMuted);
    v.on("mic-unmuted", onMicUnmuted);
    v.on("speech-start", onSpeechStart);
    v.on("speech-end", onSpeechEnd);
    return () => {
      v.off("ready", onReady);
      v.off("closed", onClose);
      v.off("error", onError);
      v.off("transcript", onTranscript);
      v.off("mic-muted", onMicMuted);
      v.off("mic-unmuted", onMicUnmuted);
      v.off("speech-start", onSpeechStart);
      v.off("speech-end", onSpeechEnd);
    };
  }, [pushTranscript, debug]);
  const start = useCallback(
    (agentId) => {
      if (debug) console.debug(`[useVoice] start with agentId: ${agentId}`);
      if (typeof window === "undefined") {
        console.warn("[useVoice] window is undefined, cannot start");
        return;
      }
      const client = createChanlClient({ agentId, apiKey, baseUrl, anonymousId, user, userHash, transport });
      voiceRef.current = client.voice;
      bindVoiceEvents();
      setMessages([]);
      setLastError(void 0);
      setStatus("connecting");
      setMode("voice");
      setDuration(0);
      currentAgentIdRef.current = agentId;
      voiceRef.current.start(agentId);
    },
    [apiKey, baseUrl, debug, anonymousId, user, userHash, transport, bindVoiceEvents]
  );
  const stop = useCallback(() => {
    if (debug) console.debug("[useVoice] stop");
    voiceRef.current?.stop();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setMode("idle");
    setStatus("closed");
  }, [debug]);
  const toggleMute = useCallback(() => {
    if (!voiceRef.current) return;
    if (debug) console.debug("[useVoice] toggleMute");
    voiceRef.current.toggleMicMute();
  }, [debug]);
  const sendMessage = useCallback(
    (text) => {
      if (!voiceRef.current || !text.trim()) return;
      if (debug) console.debug("[useVoice] sendMessage", text);
      voiceRef.current.send({
        type: "text",
        text: text.trim()
      });
      addMessage("user", text.trim());
    },
    [debug, addMessage]
  );
  useEffect(() => {
    return () => {
      if (debug) console.debug("[useVoice] cleanup on unmount");
      voiceRef.current?.stop();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [debug]);
  return {
    // Mode
    mode,
    // Voice controls (audial-legacy names)
    start,
    stop,
    toggleMute,
    sendMessage,
    // Spec-contract aliases
    startVoice: start,
    stopVoice: stop,
    // State (audial-legacy field names)
    status,
    messages,
    isMuted,
    isAgentSpeaking,
    duration,
    lastError,
    // Spec-contract aliases
    voiceState: status,
    error: lastError,
    // Low-level access
    voice: voiceRef.current
  };
}

// src/react/use-chanl.ts
function useChanl(opts) {
  const chat = useChat({
    agentId: opts.agentId,
    apiKey: opts.apiKey,
    baseUrl: opts.baseUrl,
    greeting: opts.greeting
  });
  const voice = useVoice({
    apiKey: opts.apiKey,
    baseUrl: opts.baseUrl,
    debug: opts.debug
  });
  return {
    // text
    messages: chat.messages,
    isTyping: chat.isTyping,
    send: chat.send,
    sessionReady: chat.sessionReady,
    agentConfig: chat.agentConfig,
    // voice — default to the hook's configured agentId unless caller overrides
    voiceState: voice.status,
    startVoice: (agentId) => voice.start(agentId ?? opts.agentId),
    stopVoice: voice.stop,
    toggleMute: voice.toggleMute,
    isMuted: voice.isMuted,
    isAgentSpeaking: voice.isAgentSpeaking,
    duration: voice.duration,
    voiceMessages: voice.messages,
    // shared — prefer chat error, fall back to voice error message
    error: chat.error ?? voice.lastError?.message ?? null
  };
}
function useChanlRuntime(config) {
  const ref = useRef(null);
  if (!ref.current) ref.current = createChanlRuntime(config);
  const runtime = ref.current;
  const [state, setState] = useState("idle");
  const [dataEvents, setDataEvents] = useState([]);
  useEffect(() => {
    const onStatus = (s) => setState(s);
    const onData = (d) => setDataEvents((prev) => [...prev, d]);
    runtime.on("status", onStatus);
    runtime.on("data", onData);
    return () => {
      runtime.off("status", onStatus);
      runtime.off("data", onData);
      runtime.disconnect();
    };
  }, [runtime]);
  return { runtime, state, dataEvents, sendText: runtime.sendText, send: runtime.send };
}
function ChoiceChips({ data, onRespond }) {
  const options = data?.options ?? [];
  return /* @__PURE__ */ jsx("div", { className: "chanl-choices", role: "group", children: options.map((o, i) => {
    const label = typeof o === "string" ? o : o.label;
    const value = typeof o === "string" ? o : o.value ?? o.label;
    return /* @__PURE__ */ jsx("button", { type: "button", className: "chanl-chip", onClick: () => onRespond(value), children: label }, i);
  }) });
}
function SummaryCard({ data, onRespond }) {
  const d = data ?? {};
  return /* @__PURE__ */ jsxs("div", { className: "chanl-summary", children: [
    d.title ? /* @__PURE__ */ jsx("h4", { className: "chanl-summary__title", children: d.title }) : null,
    /* @__PURE__ */ jsx("dl", { className: "chanl-summary__rows", children: (d.rows ?? []).map((r, i) => /* @__PURE__ */ jsxs("div", { style: { display: "contents" }, children: [
      /* @__PURE__ */ jsx("dt", { children: r.label }),
      /* @__PURE__ */ jsx("dd", { children: r.value })
    ] }, i)) }),
    /* @__PURE__ */ jsxs("div", { className: "chanl-summary__actions", children: [
      /* @__PURE__ */ jsx("button", { type: "button", className: "chanl-btn", onClick: () => onRespond({ choice: "cancel" }), children: d.cancelLabel ?? "Cancel" }),
      /* @__PURE__ */ jsx("button", { type: "button", className: "chanl-btn chanl-btn--primary", onClick: () => onRespond({ choice: "confirm" }), children: d.confirmLabel ?? "Confirm" })
    ] })
  ] });
}
function CardCarousel({ data, onRespond }) {
  const cards = data?.cards ?? [];
  return /* @__PURE__ */ jsx("div", { className: "chanl-carousel", children: cards.map((c, i) => /* @__PURE__ */ jsxs("div", { className: "chanl-card", children: [
    c.image ? /* @__PURE__ */ jsx("div", { className: "chanl-card__media", children: /* @__PURE__ */ jsx("img", { src: c.image, alt: c.title }) }) : null,
    /* @__PURE__ */ jsxs("div", { className: "chanl-card__body", children: [
      /* @__PURE__ */ jsx("div", { className: "chanl-card__title", children: c.title }),
      c.description ? /* @__PURE__ */ jsx("div", { className: "chanl-card__desc", children: c.description }) : null,
      c.price ? /* @__PURE__ */ jsx("div", { className: "chanl-card__price", children: c.price }) : null,
      /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          className: "chanl-btn chanl-btn--primary chanl-btn--block chanl-card__cta",
          onClick: () => onRespond({ id: c.id ?? c.title }),
          children: [
            c.cta ?? "Select",
            /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\u2192" })
          ]
        }
      )
    ] })
  ] }, c.id ?? i)) });
}
function MultiChoice({ data, onRespond }) {
  const d = data ?? {};
  const [picked, setPicked] = useState([]);
  const toggle = (o) => setPicked((p) => d.multi ? p.includes(o) ? p.filter((x) => x !== o) : [...p, o] : [o]);
  return /* @__PURE__ */ jsxs("div", { className: "chanl-quiz", children: [
    d.question ? /* @__PURE__ */ jsx("p", { className: "chanl-quiz__q", children: d.question }) : null,
    /* @__PURE__ */ jsx("div", { className: "chanl-quiz__opts", children: (d.options ?? []).map((o, i) => /* @__PURE__ */ jsxs("label", { className: `chanl-quiz__opt${picked.includes(o) ? " chanl-quiz__opt--picked" : ""}`, children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: d.multi ? "checkbox" : "radio",
          name: "chanl-quiz",
          checked: picked.includes(o),
          onChange: () => toggle(o)
        }
      ),
      /* @__PURE__ */ jsx("span", { children: o })
    ] }, i)) }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "chanl-btn chanl-btn--primary chanl-quiz__submit",
        disabled: picked.length === 0,
        onClick: () => onRespond(d.multi ? picked : picked[0]),
        children: "Submit"
      }
    )
  ] });
}
function SlotPicker({ data, onRespond }) {
  const slots = data?.slots ?? [];
  return /* @__PURE__ */ jsx("div", { className: "chanl-slots", children: slots.map((s, i) => /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      className: "chanl-slot",
      onClick: () => onRespond({ id: s.id ?? s.start, start: s.start }),
      children: s.label ?? new Date(s.start).toLocaleString()
    },
    s.id ?? i
  )) });
}
function DynamicForm({ data, onRespond }) {
  const fields = data?.fields ?? [];
  const [values, setValues] = useState({});
  return /* @__PURE__ */ jsxs(
    "form",
    {
      className: "chanl-form",
      onSubmit: (e) => {
        e.preventDefault();
        onRespond(values);
      },
      children: [
        fields.map((f) => /* @__PURE__ */ jsxs("div", { className: "chanl-field", children: [
          /* @__PURE__ */ jsxs("label", { className: "chanl-field__label", htmlFor: `chanl-field-${f.name}`, children: [
            f.label ?? f.name,
            f.required ? /* @__PURE__ */ jsx("span", { className: "chanl-field__req", children: " *" }) : null
          ] }),
          /* @__PURE__ */ jsx(
            "input",
            {
              id: `chanl-field-${f.name}`,
              name: f.name,
              type: f.type ?? "text",
              required: f.required,
              value: values[f.name] ?? "",
              onChange: (e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))
            }
          )
        ] }, f.name)),
        /* @__PURE__ */ jsx("button", { type: "submit", className: "chanl-btn chanl-btn--primary chanl-form__submit", children: "Submit" })
      ]
    }
  );
}
function RawData({ data }) {
  return /* @__PURE__ */ jsx("pre", { className: "chanl-raw", children: JSON.stringify(data, null, 2) });
}

// src/react/registry.ts
var defaultComponents = {
  choices: ChoiceChips,
  "quick-replies": ChoiceChips,
  summary: SummaryCard,
  confirm: SummaryCard,
  "offer-cards": CardCarousel,
  "product-cards": CardCarousel,
  quiz: MultiChoice,
  "booking-slots": SlotPicker,
  form: DynamicForm
};
var fallbackComponent = RawData;

// src/react/styles.ts
var WIDGET_CSS = `
.chanl-widget {
  --_radius: var(--chanl-radius, 14px);
  --_radius-sm: var(--chanl-radius-sm, 9px);
  --_card: var(--chanl-card, #ffffff);
  --_fg: var(--chanl-fg, #0b0b0c);
  --_muted: var(--chanl-muted, #6b7280);
  --_border: var(--chanl-border, #e6e6e9);
  --_accent: var(--chanl-accent, #f4f4f5);
  --_primary: var(--chanl-primary, #4f46e5);
  --_primary-fg: var(--chanl-primary-fg, #ffffff);
  --_shadow: var(--chanl-shadow, 0 1px 2px rgba(0,0,0,.04), 0 8px 24px rgba(0,0,0,.06));
  --_shadow-hover: var(--chanl-shadow-hover, 0 4px 12px rgba(0,0,0,.08), 0 14px 34px rgba(0,0,0,.12));
  --_ring: var(--chanl-ring, 0 0 0 3px color-mix(in srgb, var(--_primary) 32%, transparent));
  font-family: var(--chanl-font, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif);
  color: var(--_fg);
  font-size: 14px;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
  text-align: left;
}
@media (prefers-color-scheme: dark) {
  .chanl-widget {
    --_card: var(--chanl-card, #17171a);
    --_fg: var(--chanl-fg, #f5f5f6);
    --_muted: var(--chanl-muted, #9ca3af);
    --_border: var(--chanl-border, #2c2c31);
    --_accent: var(--chanl-accent, #232327);
    --_shadow: var(--chanl-shadow, 0 1px 2px rgba(0,0,0,.3), 0 8px 24px rgba(0,0,0,.4));
    --_shadow-hover: var(--chanl-shadow-hover, 0 4px 12px rgba(0,0,0,.4), 0 14px 34px rgba(0,0,0,.55));
  }
}
.chanl-widget * { box-sizing: border-box; }

/* \u2500\u2500 buttons \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.chanl-btn {
  appearance: none; border: 1px solid var(--_border); background: var(--_card);
  color: var(--_fg); font: inherit; font-weight: 500; cursor: pointer;
  border-radius: var(--_radius-sm); padding: 8px 14px; display: inline-flex;
  align-items: center; gap: 6px; transition: background .15s, border-color .15s, box-shadow .15s, transform .05s;
}
.chanl-btn:hover { background: var(--_accent); }
.chanl-btn:focus-visible { outline: none; box-shadow: var(--_ring); }
.chanl-btn:active { transform: scale(.98); }
.chanl-btn:disabled { opacity: .5; cursor: not-allowed; }
.chanl-btn--primary { background: var(--_primary); border-color: transparent; color: var(--_primary-fg); }
.chanl-btn--primary:hover { background: color-mix(in srgb, var(--_primary) 88%, #000); }
.chanl-btn--block { width: 100%; justify-content: center; }

/* \u2500\u2500 choice chips \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.chanl-choices { display: flex; flex-wrap: wrap; gap: 8px; }
.chanl-chip {
  appearance: none; border: 1px solid var(--_border); background: var(--_card); color: var(--_fg);
  font: inherit; font-weight: 500; cursor: pointer; border-radius: 999px; padding: 7px 14px;
  transition: background .15s, border-color .15s, box-shadow .15s, transform .05s;
}
.chanl-chip:hover { background: var(--_accent); border-color: color-mix(in srgb, var(--_primary) 40%, var(--_border)); }
.chanl-chip:focus-visible { outline: none; box-shadow: var(--_ring); }
.chanl-chip:active { transform: scale(.97); }

/* \u2500\u2500 booking slots \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.chanl-slots { display: flex; flex-wrap: wrap; gap: 8px; }
.chanl-slot {
  appearance: none; border: 1px solid var(--_border); background: var(--_card); color: var(--_fg);
  font: inherit; font-weight: 500; cursor: pointer; border-radius: var(--_radius-sm);
  padding: 9px 13px; display: inline-flex; align-items: center; gap: 7px;
  transition: background .15s, border-color .15s, box-shadow .15s, transform .05s;
}
.chanl-slot::before { content: ""; width: 7px; height: 7px; border-radius: 999px; background: var(--_primary); opacity: .85; }
.chanl-slot:hover { background: var(--_accent); border-color: color-mix(in srgb, var(--_primary) 45%, var(--_border)); }
.chanl-slot:focus-visible { outline: none; box-shadow: var(--_ring); }
.chanl-slot:active { transform: scale(.98); }

/* \u2500\u2500 card carousel (offer / product cards) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.chanl-carousel {
  display: flex; gap: 14px; overflow-x: auto; padding: 4px 2px 12px;
  scroll-snap-type: x mandatory; scrollbar-width: thin;
}
.chanl-card {
  flex: 0 0 auto; width: 216px; scroll-snap-align: start; display: flex; flex-direction: column;
  background: var(--_card); border: 1px solid var(--_border); border-radius: var(--_radius);
  box-shadow: var(--_shadow); overflow: hidden; transition: box-shadow .18s, transform .18s;
}
.chanl-card:hover { box-shadow: var(--_shadow-hover); transform: translateY(-2px); }
.chanl-card__media { aspect-ratio: 16 / 10; background: var(--_accent); overflow: hidden; }
.chanl-card__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.chanl-card__body { display: flex; flex-direction: column; gap: 4px; padding: 12px 13px 13px; flex: 1; }
.chanl-card__title { font-weight: 600; font-size: 14px; letter-spacing: -.01em; }
.chanl-card__desc { color: var(--_muted); font-size: 12.5px; }
.chanl-card__price { font-weight: 600; font-size: 15px; margin-top: 2px; font-variant-numeric: tabular-nums; }
.chanl-card__cta { margin-top: 10px; }

/* \u2500\u2500 quiz / multi-choice \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.chanl-quiz {
  background: var(--_card); border: 1px solid var(--_border); border-radius: var(--_radius);
  box-shadow: var(--_shadow); padding: 16px; display: flex; flex-direction: column; gap: 10px;
}
.chanl-quiz__q { font-weight: 600; font-size: 15px; letter-spacing: -.01em; }
.chanl-quiz__opts { display: flex; flex-direction: column; gap: 8px; }
.chanl-quiz__opt {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px; cursor: pointer;
  border: 1px solid var(--_border); border-radius: var(--_radius-sm);
  transition: background .15s, border-color .15s;
}
.chanl-quiz__opt:hover { background: var(--_accent); }
.chanl-quiz__opt--picked { border-color: var(--_primary); background: color-mix(in srgb, var(--_primary) 8%, var(--_card)); }
.chanl-quiz__opt input { accent-color: var(--_primary); width: 16px; height: 16px; }
.chanl-quiz__submit { align-self: flex-end; margin-top: 2px; }

/* \u2500\u2500 summary / confirm card \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.chanl-summary {
  background: var(--_card); border: 1px solid var(--_border); border-radius: var(--_radius);
  box-shadow: var(--_shadow); padding: 16px; display: flex; flex-direction: column; gap: 12px;
}
.chanl-summary__title { font-weight: 600; font-size: 15px; letter-spacing: -.01em; margin: 0; }
.chanl-summary__rows { display: grid; grid-template-columns: auto 1fr; gap: 8px 16px; margin: 0; }
.chanl-summary__rows dt { color: var(--_muted); font-size: 13px; }
.chanl-summary__rows dd { margin: 0; text-align: right; font-weight: 500; font-variant-numeric: tabular-nums; }
.chanl-summary__actions { display: flex; gap: 8px; justify-content: flex-end; }

/* \u2500\u2500 dynamic form \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.chanl-form {
  background: var(--_card); border: 1px solid var(--_border); border-radius: var(--_radius);
  box-shadow: var(--_shadow); padding: 16px; display: flex; flex-direction: column; gap: 12px;
}
.chanl-field { display: flex; flex-direction: column; gap: 5px; }
.chanl-field__label { font-size: 13px; font-weight: 500; color: var(--_fg); }
.chanl-field__req { color: var(--_primary); }
.chanl-field input {
  font: inherit; color: var(--_fg); background: var(--_card); border: 1px solid var(--_border);
  border-radius: var(--_radius-sm); padding: 9px 11px; transition: border-color .15s, box-shadow .15s;
}
.chanl-field input:focus-visible { outline: none; border-color: var(--_primary); box-shadow: var(--_ring); }
.chanl-form__submit { align-self: flex-end; }

/* \u2500\u2500 raw fallback \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.chanl-raw {
  background: var(--_accent); border: 1px solid var(--_border); border-radius: var(--_radius-sm);
  padding: 12px; font-size: 12.5px; overflow-x: auto; margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
`;
var injected = false;
function injectWidgetStyles() {
  if (injected) return;
  if (typeof document === "undefined") return;
  if (document.getElementById("chanl-widget-styles")) {
    injected = true;
    return;
  }
  const el = document.createElement("style");
  el.id = "chanl-widget-styles";
  el.textContent = WIDGET_CSS;
  document.head.appendChild(el);
  injected = true;
}
function WidgetView(props) {
  const { event, components, runtime } = props;
  useEffect(() => {
    injectWidgetStyles();
  }, []);
  const key = event.widget ?? "";
  const Comp = components?.[key] ?? defaultComponents[key] ?? fallbackComponent;
  return /* @__PURE__ */ jsx("div", { className: "chanl-widget", children: /* @__PURE__ */ jsx(Comp, { data: event.data, onRespond: (value) => runtime.send({ type: "data", widget: event.widget, value }) }) });
}

export { ChanlEmbed, ChanlWidget, WIDGET_CSS, WidgetView, defaultComponents, fallbackComponent, injectWidgetStyles, useChanl, useChanlRuntime, useChat, useVoice };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map